/**
 * Spec 158 (CC-54030) — the Generate Bills run, as pure functions the page drives.
 *
 * cb-api generates ONE matter's bill per POST /invoices/generate (CB's Billing Home
 * path: GetResultSetId, then GenerateBills). The page sends the selected matters one
 * at a time, in order, and this module decides what each answer means.
 *
 * The rule that everything here serves: a bill CB may have created must never be
 * reported as "not created", because the obvious response to "not created" is to
 * try again, and a second run bills the matter again. So:
 *  - only an explicit `outcome: 'not-sent'` from cb-api means nothing was generated;
 *  - every other failure — a timeout, an edge 524, an HTML page, a body we cannot
 *    read — is UNKNOWN, and stops the run;
 *  - CB's "no bill" (`outcome: 'none'`) is NOT "nothing was created": GenerateBillsSync
 *    creates the bill inside its proc and, when a later step for that bill throws, logs
 *    it and leaves the bill out of the ids it returns without removing it
 *    (AccountingHelper.cs:3825-3828). So "none" also stops the run and locks the matter;
 *  - nothing is retried, ever. A matter with a created, none or unknown outcome is
 *    locked for the rest of the session (utils/billRun.js keeps the locks).
 */

export const GENERATE_TIMEOUT_MS = 120000

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'
const isBillId = (v) => typeof v === 'string' && GUID_RE.test(v) && v !== EMPTY_GUID

/** Outcomes a result can carry. `refused` = cb-api said nothing was generated. */
export const OUTCOME = Object.freeze({
  CREATED: 'created',
  NONE: 'none',
  REFUSED: 'refused',
  UNKNOWN: 'unknown',
  NOT_ATTEMPTED: 'not-attempted',
})

const UNKNOWN_TAIL = 'It may still have created a bill. Check this matter’s bills in Centerbase before generating it again.'

/**
 * A 200 from POST /invoices/generate. Anything that is not exactly the documented
 * shape is UNKNOWN: the request reached CB, so "nothing happened" cannot be assumed.
 * @returns {{ outcome: string, invoiceIds?: string[], message?: string }}
 */
export function classifyGenerateAnswer(body) {
  const d = body?.data
  if (d && Array.isArray(d.invoiceIds) && d.invoiceIds.every(isBillId)) {
    if (d.outcome === OUTCOME.CREATED && d.invoiceIds.length > 0) return { outcome: OUTCOME.CREATED, invoiceIds: d.invoiceIds }
    if (d.outcome === OUTCOME.NONE && d.invoiceIds.length === 0) {
      // Not "no bill was created", and not "nothing was eligible": CB's generator makes a
      // bill or statement for every matter it is sent, and applies no billing setting at
      // that point. It names none when another session holds the matter's generation
      // lock, or when a step failed — possibly after the bill was created
      // (AccountingHelper.cs:3825-3828). Say what CB said, and only what it can mean.
      return {
        outcome: OUTCOME.NONE,
        invoiceIds: [],
        message:
          'Centerbase reported no new bill for this matter. It answers this way when another session is generating the same matter at that moment, or when a step failed — possibly after the bill was created; it then sends the details to your notifications. Check this matter’s bills in Centerbase before generating it again.',
      }
    }
  }
  return { outcome: OUTCOME.UNKNOWN, message: `Centerbase’s answer for this matter could not be read. ${UNKNOWN_TAIL}` }
}

/**
 * A rejected POST /invoices/generate (an axios error). cb-api says which side of the
 * write a failure fell on; without that word it is UNKNOWN.
 * @returns {{ outcome: string, message: string }}
 */
export function classifyGenerateError(err) {
  const resp = err?.response
  const said = typeof resp?.data?.error === 'string' && resp.data.error ? resp.data.error : null
  if (resp?.data?.outcome === 'not-sent') {
    return { outcome: OUTCOME.REFUSED, message: said || 'cb-api refused the request, so nothing was generated.' }
  }
  if (resp?.data?.outcome === 'unknown' && said) return { outcome: OUTCOME.UNKNOWN, message: said }
  if (resp) {
    return { outcome: OUTCOME.UNKNOWN, message: `The request was answered with HTTP ${resp.status}${said ? ` (${said})` : ''}. ${UNKNOWN_TAIL}` }
  }
  return { outcome: OUTCOME.UNKNOWN, message: `No answer came back${err?.code === 'ECONNABORTED' ? ' before the page stopped waiting' : ''}. ${UNKNOWN_TAIL}` }
}

/**
 * Generate each matter's bill in turn.
 *
 * Stops — leaving the rest NOT ATTEMPTED — on the first outcome that is not a
 * confirmed bill (a refusal, "none" or unknown), when the browser reports itself
 * offline before a matter is sent, or when the user asks it to stop. Never retries. `generate(request)` resolves to cb-api's body or
 * rejects with its axios error.
 *
 * @param {{
 *   matters: Array<{ matterId: string, matterName: string }>,
 *   period: { settingsId: number, startDate: string|null, endDate: string },
 *   issueDate: string,
 *   generate: (request: object) => Promise<unknown>,
 *   onResult?: (result: object, index: number) => void,
 *   onStart?: (matter: object, index: number) => void,
 *   shouldStop?: () => boolean,
 *   isOnline?: () => boolean,
 * }} args
 */
export async function runGeneration({ matters, period, issueDate, generate, onResult, onStart, shouldStop, isOnline }) {
  const results = []
  let stopReason = null
  for (let i = 0; i < matters.length; i += 1) {
    const m = matters[i]
    if (!stopReason && shouldStop?.()) stopReason = 'stopped'
    if (!stopReason && isOnline && !isOnline()) stopReason = 'offline'
    if (stopReason) {
      const r = {
        matterId: m.matterId,
        matterName: m.matterName,
        outcome: OUTCOME.NOT_ATTEMPTED,
        message: stopReason === 'offline'
          ? 'Not sent: the browser went offline before this matter, so nothing was asked of Centerbase for it.'
          : stopReason === 'stopped'
            ? 'Not sent: you stopped the run before this matter.'
            : 'Not sent: the run stopped at an earlier matter.',
      }
      results.push(r)
      onResult?.(r, i)
      continue
    }
    onStart?.(m, i)
    let r
    try {
      const body = await generate({
        matterId: m.matterId,
        settingsId: period.settingsId,
        ...(period.startDate ? { startDate: period.startDate } : {}),
        endDate: period.endDate,
        issueDate,
      })
      r = { matterId: m.matterId, matterName: m.matterName, ...classifyGenerateAnswer(body) }
    } catch (err) {
      r = { matterId: m.matterId, matterName: m.matterName, ...classifyGenerateError(err) }
    }
    results.push(r)
    onResult?.(r, i)
    // Only a confirmed bill lets the run go on. The confirm step promises exactly this.
    if (r.outcome !== OUTCOME.CREATED) stopReason = 'failed'
  }
  return results
}

/**
 * Matters that must not be offered again this session: CB created a bill, or may have
 * ("none" included — see the module comment). Only a refusal or a matter never sent
 * stays selectable. (At most one run at a time is utils/billRun.js's job: its guard
 * lives at module level, so it holds across a remount of the page as well as across
 * presses that land before React re-renders.)
 */
export const lockingOutcome = (outcome) =>
  outcome === OUTCOME.CREATED || outcome === OUTCOME.NONE || outcome === OUTCOME.UNKNOWN

/**
 * After the run, read the list again and say what CB now shows. The list's last bill
 * is POSITIVE evidence only:
 *  - a created result whose bill is now the matter's last bill gets that bill's number;
 *  - an unknown or "none" result whose matter now shows a DIFFERENT last bill than
 *    before the run is reported as "a new bill now shows" — almost certainly the one CB
 *    did not confirm.
 * An unchanged last bill proves nothing (a bill dated before the last one does not
 * become "last", and CB may still be generating), so the outcome stays as it was.
 *
 * @param {Array<object>} results  runGeneration's results
 * @param {Map<string, string|null>} lastBillBefore  matterId -> last bill id before the run
 * @param {Array<object>|null} rowsAfter  the re-read list's rows, or null when it failed
 */
export function reconcileWithList(results, lastBillBefore, rowsAfter) {
  if (!Array.isArray(rowsAfter)) return results
  const byId = new Map(rowsAfter.map((r) => [String(r.matterId).toLowerCase(), r]))
  return results.map((res) => {
    const row = byId.get(String(res.matterId).toLowerCase())
    const after = row?.lastBill
    if (!after?.id) return res
    if (res.outcome === OUTCOME.CREATED && res.invoiceIds?.some((id) => id.toLowerCase() === after.id.toLowerCase())) {
      return { ...res, billNumber: after.number || null }
    }
    if (res.outcome === OUTCOME.UNKNOWN || res.outcome === OUTCOME.NONE) {
      const before = lastBillBefore.get(String(res.matterId).toLowerCase()) ?? null
      if ((before ?? '').toLowerCase() !== after.id.toLowerCase()) {
        return { ...res, newBillSeen: { id: after.id, number: after.number || null } }
      }
    }
    return res
  })
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
/** CB's figure as it is, to the cent. Negative stays negative. */
export const money = (v) => (typeof v === 'number' && Number.isFinite(v) ? usd.format(v) : '—')
export const quantity = (v) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(2) : '—')

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/
/** A real calendar day as YYYY-MM-DD, or null. */
export const realDay = (v) => {
  const m = ISO_DAY.exec(String(v ?? ''))
  if (!m) return null
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]) ? m[0] : null
}

/**
 * The period as cb-api takes it, or an error to show. An empty start date means
 * "on or before the end date", which is the shape of Billing Home's own default.
 */
export function readPeriodInputs(startDate, endDate) {
  const end = realDay(endDate)
  if (!end) return { error: 'Choose the last date of entries to bill.' }
  if (startDate === '' || startDate == null) return { startDate: null, endDate: end }
  const start = realDay(startDate)
  if (!start) return { error: 'The first date is not a real date. Clear it to bill everything up to the last date.' }
  if (start > end) return { error: 'The first date is after the last date.' }
  return { startDate: start, endDate: end }
}
